import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
} from '@mui/material';
import { TargetData } from 'src/types/targets';
import { TargetKey, TARGET_TITLES } from 'src/types/targets-constants';
import axiosInstance from 'src/utils/axios';
import { useSnackbar } from 'notistack';
import { getTargetByKey } from 'src/api/admin';
import { useLocales } from 'src/locales';

interface TargetsModalProps {
  open: boolean;
  onClose: () => void;
  targetKey: TargetKey;
  onTargetUpdated: (key: TargetKey, updated: TargetData) => void;
}

export default function TargetsModal({
  open,
  onClose,
  targetKey,
  onTargetUpdated,
}: TargetsModalProps) {
  const [formData, setFormData] = useState<{ targetBottle: string; euroTarget: string }>({
    targetBottle: '',
    euroTarget: '',
  });
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useLocales('targetsPage');

  useEffect(() => {
    if (open && targetKey) {
      getTargetByKey(targetKey)
        .then((data) => {
          if (data) {
            setFormData({
              targetBottle: data.targetBottle?.toString() || '',
              euroTarget: data.euroTarget?.toString() || '',
            });
          }
        })
        .catch((err) => {
          console.error('Failed to fetch target', err);
          enqueueSnackbar(t('failed_to_fetch_target_data'), { variant: 'error' });
        });
    } else {
      setFormData({ targetBottle: '', euroTarget: '' });
    }
  }, [open, targetKey, enqueueSnackbar, t]);

  const handleChange = (field: 'targetBottle' | 'euroTarget', value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    const targetBottleNum = Number(formData.targetBottle);
    const euroTargetNum = Number(formData.euroTarget);

    if (
      Number.isNaN(targetBottleNum) ||
      Number.isNaN(euroTargetNum) ||
      targetBottleNum < 0 ||
      targetBottleNum > 100 ||
      euroTargetNum < 0 ||
      euroTargetNum > 100 ||
      !Number.isInteger(targetBottleNum)
    ) {
      enqueueSnackbar(t('please_enter_valid_values'), {
        variant: 'warning',
      });
      return;
    }

    setLoading(true);
    try {
      const res = await axiosInstance.post('/admin/targets', {
        key: targetKey,
        targetBottle: targetBottleNum,
        euroTarget: euroTargetNum,
      });

      const updatedTargetData = res.data.data;
      const finalTargetData = {
        ...updatedTargetData,
        euroTarget: parseFloat(updatedTargetData.euroTarget),
      };
      onTargetUpdated(targetKey, finalTargetData);
      enqueueSnackbar(t('target_saved_successfully'), { variant: 'success' });
      onClose();
    } catch (error) {
      console.error('Failed to save target', error);
      enqueueSnackbar(t('failed_to_save_target,_please_try_again.'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {t('set_target_for')} {t(TARGET_TITLES[targetKey])}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 2 }}>
          <TextField
            label={t('target_bottles')}
            type="number"
            value={formData.targetBottle}
            onChange={({ target: { value } }) => {
              if (/^\d*$/.test(value)) {
                handleChange('targetBottle', value);
              }
            }}
            fullWidth
            slotProps={{
              htmlInput: { min: 0, max: 100, step: 1 },
            }}
          />

          <TextField
            label={t('euro_target_percentage')}
            type="number"
            value={formData.euroTarget}
            onChange={(e) => handleChange('euroTarget', e.target.value)}
            fullWidth
            slotProps={{
              htmlInput: { min: 0, max: 100, step: 0.1 },
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={loading}>
          {loading ? t('Saving...') : t('Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
